import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { api } from '../../../shared/services/API'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import { METODO_PAGAMENTO_LABEL, formatDate, formatMoney, type Pagamento } from '../subscricoesModel'

export default function PagamentosList() {
    const [rows, setRows] = useState<Pagamento[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-pagamentos:table-page', 20)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const fetch = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Page<Pagamento>>('/api/admin/subscricoes/pagamentos', {
            params: { page, size: rowsPerPage, sort: 'pagoEm,desc' },
        })
            .then((r) => {
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => setError('Não foi possível carregar os pagamentos.'))
            .finally(() => setIsLoading(false))
    }, [page, rowsPerPage])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch alterna a sua própria flag de loading
        fetch()
    }, [fetch])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Pagamentos</Typography>
                <Typography variant="body2" color="text.secondary">
                    {total} {total === 1 ? 'pagamento' : 'pagamentos'} · registados a partir de cada cobrança
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Data</TableCell>
                                <TableCell>Empresa</TableCell>
                                <TableCell>Cobrança</TableCell>
                                <TableCell>Método</TableCell>
                                <TableCell>Referência</TableCell>
                                <TableCell align="right">Valor</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>Nenhum pagamento.</TableCell></TableRow>
                            ) : (
                                rows.map((p) => (
                                    <TableRow key={p.id} hover>
                                        <TableCell>{formatDate(p.pagoEm)}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{p.tenantNome ?? p.tenantId}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{p.cobrancaReferencia ?? '—'}</TableCell>
                                        <TableCell>{METODO_PAGAMENTO_LABEL[p.metodo]}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{p.referencia ?? '—'}</TableCell>
                                        <TableCell align="right">{formatMoney(p.valor)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>
        </Box>
    )
}
